import { useCallback, useEffect, useRef, useState } from 'react';
import { operatorConsole } from '../../lib/api/operatorConsole';
import { ARCHIVE_FAILED_MESSAGE, ArchiveTimeoutError, archiveErrorMessage } from './mediaKitErrors';
import { triggerDownload } from './triggerDownload';

const POLL_INTERVAL_MS = 2000;
const GIVE_UP_AFTER_MS = 10 * 60 * 1000;

export type ArchiveDownloadState =
  | { phase: 'idle' }
  | { phase: 'preparing' }
  | { phase: 'started' }
  | { phase: 'failed'; message: string };

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** POST the archive, poll it while pending, then hand its URL to the browser.
 *  Leaving the page aborts the poll. */
export function useArchiveDownload(packId: number) {
  const [state, setState] = useState<ArchiveDownloadState>({ phase: 'idle' });
  const controller = useRef<AbortController | null>(null);

  useEffect(() => () => controller.current?.abort(), []);

  const start = useCallback(async () => {
    controller.current?.abort();
    const current = new AbortController();
    controller.current = current;
    const { signal } = current;
    const giveUpAt = Date.now() + GIVE_UP_AFTER_MS;
    setState({ phase: 'preparing' });

    try {
      let archive = await operatorConsole.mediaKit.requestArchive(packId, signal);
      while (archive.status === 'pending') {
        if (Date.now() >= giveUpAt) throw new ArchiveTimeoutError();
        await wait(POLL_INTERVAL_MS, signal);
        archive = await operatorConsole.mediaKit.getArchive(archive.token, signal);
      }
      if (archive.status === 'ready' && archive.url) {
        triggerDownload(archive.url);
        setState({ phase: 'started' });
      } else {
        setState({ phase: 'failed', message: ARCHIVE_FAILED_MESSAGE });
      }
    } catch (err) {
      if (signal.aborted) return;
      setState({ phase: 'failed', message: archiveErrorMessage(err) });
    }
  }, [packId]);

  return { state, start };
}
